import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { showError } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Building2,
  Users,
  TrendingUp,
  Loader2,
  AlertCircle,
  BarChart3,
  UserCheck,
  X,
  Settings2,
  Landmark,
} from 'lucide-react';
import apiClient from '@/api/client';
import DataTable from '@/components/datatable/DataTable';
import { OVERVIEW_COLUMNS, formatCurrency, formatPercent } from '@/components/datatable/overviewColumns';
import { useColumnSettings } from '@/hooks/useColumnSettings';
import ColumnSettingsModal from '@/components/datatable/ColumnSettingsModal';


const ProjectsOverviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPageAccess } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Initialize filter state from URL params for back/forward navigation persistence
  const [selectedClient, setSelectedClient] = useState(() => searchParams.get('client') || null);
  const [kpiFilter, setKpiFilter] = useState(() => searchParams.get('kpi') || null);
  // Use manager_id (stable) instead of name (can change/duplicate)
  const [managerFilter, setManagerFilter] = useState(() => searchParams.get('manager_id') || null);
  // Stage 11.5: pcc_status filters
  const [showArchived, setShowArchived] = useState(() => searchParams.get('archived') === 'true');
  const [showClosed, setShowClosed] = useState(() => searchParams.get('closed') === 'true');

  // Блоки "СУБПОДРЯД И НАЛОГИ" и "ИТОГОВАЯ КАЛЬКУЛЯЦИЯ" скрыты для Senior Manager
  const userRoleLower = user?.role?.toLowerCase();
  const canViewFinanceBlocks = userRoleLower === 'admin' || userRoleLower === 'finance_admin';
  // УДП дашборд доступен finance_admin и senior_manager
  const canAccessAgentDashboard = ['finance_admin', 'senior_manager'].includes(userRoleLower);

  // Column settings state
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  // Column settings hook
  const {
    visibleColumnKeys,
    columnOrder,
    loading: columnSettingsLoading,
    saving: columnSettingsSaving,
    isCustomized,
    toggleColumn,
    reorderColumns,
    applySettings,
    resetSettings,
    getOrderedColumns,
  } = useColumnSettings('projects-overview', OVERVIEW_COLUMNS);

  // Role filter for columns (hide finance columns for non-finance roles)
  const roleFilter = useCallback(
    (col) => !col.finance || canViewFinanceBlocks,
    [canViewFinanceBlocks]
  );

  // Build visible columns based on role AND user settings
  const visibleColumns = useMemo(
    () => getOrderedColumns(OVERVIEW_COLUMNS, roleFilter),
    [getOrderedColumns, roleFilter]
  );

  // Handle apply column settings
  const handleApplyColumnSettings = useCallback(() => {
    applySettings();
    setColumnSettingsOpen(false);
  }, [applySettings]);

  // Handle reset column settings
  const handleResetColumnSettings = useCallback(async () => {
    await resetSettings();
    setColumnSettingsOpen(false);
  }, [resetSettings]);

  // Handle column reorder from table header drag-and-drop
  const handleColumnReorder = useCallback((fromKey, toKey) => {
    reorderColumns(fromKey, toKey);
    // Auto-save after reorder
    setTimeout(() => applySettings(), 0);
  }, [reorderColumns, applySettings]);

  // Get responsible_ids filter from URL (from dashboard "Нагрузка команды" click)
  const responsibleIdsParam = searchParams.get('responsible_ids');

  // Sync filter state to URL for back/forward navigation persistence
  // IMPORTANT: Clone existing params to preserve page/sort/search/etc
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Update only the filter keys we control (preserve all others)
    if (selectedClient) params.set('client', selectedClient);
    else params.delete('client');

    if (kpiFilter) params.set('kpi', kpiFilter);
    else params.delete('kpi');

    if (managerFilter) params.set('manager_id', managerFilter);
    else params.delete('manager_id');

    // Stage 11.5: pcc_status filter params
    if (showArchived) params.set('archived', 'true');
    else params.delete('archived');

    if (showClosed) params.set('closed', 'true');
    else params.delete('closed');

    // responsible_ids is managed externally (from dashboard), don't touch it here

    // Only update if params actually changed (avoid infinite loop)
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedClient, kpiFilter, managerFilter, showArchived, showClosed, searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    loadData(selectedClient, responsibleIdsParam, showArchived, showClosed);
  }, [selectedClient, responsibleIdsParam, showArchived, showClosed]);

  const loadData = async (client, responsibleIds, includeArchived, includeClosed) => {
    try {
      setLoading(true);
      const params = {};
      if (client) params.client = client;
      if (responsibleIds) params.responsible_ids = responsibleIds;
      // Stage 11.5: pcc_status filters
      if (includeArchived) params.include_archived = true;
      if (includeClosed) params.include_closed = true;
      const response = await apiClient.get('/projects/by-client', { params });
      setData(response.data);
    } catch (error) {
      showError(error, 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Clear responsible_ids filter
  const clearManagerFilter = () => {
    setSearchParams({});
  };
  
  // Client list with "ALL" at top
  const clientList = useMemo(() => {
    if (!data?.clients) return [];
    
    // Sort: by total_kp desc, "Без заказчика" always last
    const sorted = [...data.clients].sort((a, b) => {
      if (a.client_name === 'Без заказчика') return 1;
      if (b.client_name === 'Без заказчика') return -1;
      return (b.total_kp || 0) - (a.total_kp || 0);
    });
    
    return sorted;
  }, [data?.clients]);
  
  // Total KPI for "ALL" view
  const totalKpi = useMemo(() => {
    if (!data?.clients) return null;

    const totals = data.clients.reduce((acc, c) => ({
      project_count: acc.project_count + (c.project_count || 0),
      total_kp: acc.total_kp + (c.total_kp || 0),
      total_entry_cost: acc.total_entry_cost + (c.total_entry_cost || 0),
      total_contract: acc.total_contract + (c.total_contract || 0),
      total_revenue: acc.total_revenue + (c.total_revenue || 0),
      critical_count: acc.critical_count + (c.critical_count || 0),
      _margin_sum: acc._margin_sum + (c.avg_margin_percent || 0) * (c.project_count || 0),
      _margin_count: acc._margin_count + ((c.avg_margin_percent > 0 ? c.project_count : 0) || 0),
    }), { project_count: 0, total_kp: 0, total_entry_cost: 0, total_contract: 0, total_revenue: 0, critical_count: 0, _margin_sum: 0, _margin_count: 0 });

    totals.avg_margin_percent = totals._margin_count > 0
      ? totals._margin_sum / totals._margin_count
      : 0;

    return totals;
  }, [data?.clients]);
  
  const kpi = selectedClient ? data?.kpi : (data?.kpi || totalKpi);

  // Toggle KPI filter (click again to deselect)
  const toggleKpiFilter = (filter) => {
    setKpiFilter(prev => prev === filter ? null : filter);
  };

  // Filter and sort projects based on active KPI filter and manager filter
  const filteredProjects = useMemo(() => {
    let projects = data?.projects || [];

    // Apply manager filter first (by responsible_id for stability)
    if (managerFilter) {
      projects = projects.filter(p => p.responsible_id === managerFilter);
    }

    if (!kpiFilter) return projects;

    switch (kpiFilter) {
      case 'kp':
        return [...projects]
          .filter(p => p.kp_amount_with_vat > 0)
          .sort((a, b) => (b.kp_amount_with_vat || 0) - (a.kp_amount_with_vat || 0));
      case 'entry':
        return [...projects]
          .filter(p => p.entry_cost_with_vat > 0)
          .sort((a, b) => (b.entry_cost_with_vat || 0) - (a.entry_cost_with_vat || 0));
      case 'contract':
        return [...projects]
          .filter(p => p.contract_amount_with_vat > 0)
          .sort((a, b) => (b.contract_amount_with_vat || 0) - (a.contract_amount_with_vat || 0));
      case 'margin':
        return [...projects]
          .filter(p => p.result_margin_percent != null)
          .sort((a, b) => (b.result_margin_percent || 0) - (a.result_margin_percent || 0));
      case 'critical':
        return projects.filter(p => p.health_status === 'red');
      default:
        return projects;
    }
  }, [data?.projects, kpiFilter, managerFilter]);

  const kpiFilterLabels = {
    kp: 'с суммой КП',
    entry: 'с входом',
    contract: 'с договором',
    margin: 'по марже',
    critical: 'критичные',
  };
  
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="projects-overview-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {responsibleIdsParam ? 'Проекты менеджеров' : 'Портфель проектов'}
                </h1>
                <p className="text-sm text-gray-500">
                  {responsibleIdsParam ? 'Фильтр по нагрузке команды' : 'Аналитика по заказчикам'}
                </p>
              </div>
              {responsibleIdsParam && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearManagerFilter}
                  className="flex items-center gap-1 text-gray-600"
                >
                  <X className="w-4 h-4" />
                  Сбросить фильтр
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {canAccessAgentDashboard && (
                <a href="/udp-dashboard.html" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="udp-dashboard-button"
                  >
                    <Landmark className="w-4 h-4" />
                    УДП
                  </Button>
                </a>
              )}
              {hasPageAccess('/projects/new') && (
                <Button onClick={() => navigate('/projects/new')}>
                  + Новый проект
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content: Master-Detail Layout */}
      <div className="w-full flex h-[calc(100vh-80px)]">
        
        {/* Left Panel: Client Navigation */}
        <aside className="w-64 bg-gray-50 border-r flex-shrink-0">
          <div className="py-2 px-4 border-b bg-white">
            <h2 className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Заказчики
            </h2>
          </div>

          <ScrollArea className="h-[calc(100vh-130px)]">
            <div className="p-1.5">
              {/* ALL clients option */}
              <button
                onClick={() => setSelectedClient(null)}
                className={`w-full text-left px-3 py-2 rounded mb-0.5 transition-colors ${
                  selectedClient === null
                    ? 'bg-white shadow-sm border-l-2 border-blue-500'
                    : 'hover:bg-white/60'
                }`}
                data-testid="client-all"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">ВСЕ</span>
                  <Badge variant="secondary" className="text-xs h-5 px-1.5">{totalKpi?.project_count || 0}</Badge>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {formatCurrency(totalKpi?.total_revenue)}
                </div>
              </button>

              <Separator className="my-1.5" />

              {/* Client list */}
              {clientList.map((client) => (
                <button
                  key={client.client_name}
                  onClick={() => setSelectedClient(client.client_name)}
                  className={`w-full text-left px-3 py-2 rounded mb-0.5 transition-colors ${
                    selectedClient === client.client_name
                      ? 'bg-white shadow-sm border-l-2 border-blue-500'
                      : 'hover:bg-white/60'
                  }`}
                  data-testid={`client-${client.client_name}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium truncate max-w-[140px] ${
                      client.client_name === 'Без заказчика' ? 'text-gray-400 italic' : ''
                    }`}>
                      {client.client_name}
                    </span>
                    <div className="flex items-center gap-1">
                      {client.critical_count > 0 && (
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                      )}
                      <Badge variant="secondary" className="text-xs h-5 px-1.5">
                        {client.project_count}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {formatCurrency(client.total_revenue)}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </aside>
        
        {/* Right Panel: Content */}
        <main className="flex-1 flex flex-col overflow-hidden">

          {/* Block A: KPI Summary — fixed area */}
          <div className="flex-shrink-0 bg-gray-50 py-3 px-6">
          <div className={`grid gap-3 ${canViewFinanceBlocks ? 'grid-cols-6' : 'grid-cols-5'}`}>
            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                !kpiFilter ? 'ring-2 ring-gray-300' : ''
              }`}
              onClick={() => setKpiFilter(null)}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                  <BarChart3 className="w-3.5 h-3.5 shrink-0" />
                  Всего проектов
                </div>
                <div className="text-xl font-bold whitespace-nowrap">{kpi?.project_count || 0}</div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                kpiFilter === 'kp' ? 'ring-2 ring-blue-500 shadow-md' : 'border-blue-200 bg-blue-50/30'
              }`}
              onClick={() => toggleKpiFilter('kp')}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-1.5 text-blue-600 text-xs mb-1">
                  <span className="shrink-0 font-bold">₸</span>
                  Сумма КП с НДС
                </div>
                <div className="text-xl font-bold text-blue-700 whitespace-nowrap">
                  {formatCurrency(kpi?.total_kp)}
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                kpiFilter === 'entry' ? 'ring-2 ring-blue-500 shadow-md' : ''
              }`}
              onClick={() => toggleKpiFilter('entry')}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                  <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                  Вход с НДС
                </div>
                <div className="text-xl font-bold whitespace-nowrap">{formatCurrency(kpi?.total_entry_cost)}</div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                kpiFilter === 'contract' ? 'ring-2 ring-blue-500 shadow-md' : ''
              }`}
              onClick={() => toggleKpiFilter('contract')}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                  <span className="shrink-0 font-bold">₸</span>
                  Сумма договоров
                </div>
                <div className="text-xl font-bold whitespace-nowrap">{formatCurrency(kpi?.total_contract)}</div>
              </CardContent>
            </Card>

            {/* Средняя маржа - скрыто для Senior Manager */}
            {canViewFinanceBlocks && (
              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${
                  kpiFilter === 'margin' ? 'ring-2 ring-blue-500 shadow-md' : ''
                }`}
                onClick={() => toggleKpiFilter('margin')}
              >
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                    <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                    Средняя маржа
                  </div>
                  <div className="text-xl font-bold text-green-600 whitespace-nowrap">
                    {formatPercent(kpi?.avg_margin_percent)}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                kpiFilter === 'critical'
                  ? 'ring-2 ring-red-500 shadow-md border-red-300 bg-red-50'
                  : kpi?.critical_count > 0 ? 'border-red-200 bg-red-50' : ''
              }`}
              onClick={() => toggleKpiFilter('critical')}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  Критичных
                </div>
                <div className={`text-xl font-bold whitespace-nowrap ${kpi?.critical_count > 0 ? 'text-red-600' : ''}`}>
                  {kpi?.critical_count || 0}
                </div>
              </CardContent>
            </Card>
          </div>
          </div>

          {/* Block B: Projects Table — scrollable area */}
          <div className="flex-1 overflow-auto px-6 pb-6 space-y-6">

          {/* Stage 11.5: pcc_status checkboxes - вынесены из Card чтобы всегда были видны */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setColumnSettingsOpen(true)}
                className={`h-7 w-7 p-0 ${isCustomized ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                title="Настроить колонки"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
              <span className="text-lg font-semibold">
                {selectedClient ? `Проекты: ${selectedClient}` : 'Все проекты'}
              </span>
              {managerFilter && (
                <>
                  <span className="text-gray-400">—</span>
                  <Badge variant="secondary" className="text-xs">
                    {data?.team?.find(m => m.responsible_id === managerFilter)?.name || managerFilter} ({filteredProjects.length})
                  </Badge>
                  <button onClick={() => setManagerFilter(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
              {kpiFilter && (
                <>
                  <span className="text-gray-400">—</span>
                  <Badge variant="secondary" className="text-xs">
                    {kpiFilterLabels[kpiFilter]} ({filteredProjects.length})
                  </Badge>
                  <button onClick={() => setKpiFilter(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-1.5 cursor-pointer text-gray-600 hover:text-gray-900">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Показать архив
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-gray-600 hover:text-gray-900">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Показать закрытые
              </label>
            </div>
          </div>

          <Card style={{ minWidth: 'fit-content' }}>
            <CardContent className="p-0 sm:px-6 sm:pb-6">
              <DataTable
                columns={visibleColumns}
                data={filteredProjects}
                onRowClick={(project) => navigate(`/projects/${project.id}`, { state: { returnUrl: location.pathname + location.search } })}
                storageKey="projects-overview"
                emptyMessage={kpiFilter ? 'Нет проектов по выбранному фильтру' : 'Нет проектов'}
                onColumnReorder={handleColumnReorder}
              />
            </CardContent>
          </Card>
          
          {/* Block C: Team (Resource Context) */}
          {selectedClient && data?.team?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Команда заказчика
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {data.team.map((member) => (
                    <div 
                      key={member.responsible_id}
                      className={`p-4 rounded-lg border ${
                        member.is_overloaded 
                          ? 'border-red-200 bg-red-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                      data-testid={`team-member-${member.responsible_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className={`w-5 h-5 ${member.is_overloaded ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className="font-medium">{member.name}</span>
                      </div>
                      <div className="text-sm text-gray-600 font-medium">
                        Нагрузка: <span className="text-blue-600">{member.projects_for_client}</span> / <span className="text-gray-900">{member.total_active_projects}</span>
                        <span className="text-xs text-gray-400 ml-1">(здесь/всего)</span>
                      </div>
                      {member.is_overloaded && (
                        <div className="text-xs text-red-600 font-medium mt-1">⚠️ Перегруз (&gt;5 проектов)</div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Team for ALL view */}
          {!selectedClient && data?.team?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Загрузка менеджеров
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {data.team.map((member) => (
                    <div
                      key={member.responsible_id}
                      onClick={() => setManagerFilter(prev => prev === member.responsible_id ? null : member.responsible_id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        managerFilter === member.responsible_id
                          ? 'ring-2 ring-blue-500 border-blue-300 bg-blue-50'
                          : member.is_overloaded
                            ? 'border-red-200 bg-red-50 hover:shadow-md'
                            : 'border-gray-200 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className={`w-5 h-5 ${member.is_overloaded ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className="font-medium">{member.name}</span>
                      </div>
                      <div className={`text-sm ${member.is_overloaded ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        Активных проектов: <span className="font-medium">{member.total_active_projects}</span>
                        {member.is_overloaded && (
                          <span className="ml-2">⚠️ Перегруз</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          </div>

        </main>
      </div>

      {/* Column Settings Modal */}
      <ColumnSettingsModal
        open={columnSettingsOpen}
        onOpenChange={setColumnSettingsOpen}
        columns={OVERVIEW_COLUMNS}
        visibleColumnKeys={visibleColumnKeys}
        columnOrder={columnOrder}
        onToggleColumn={toggleColumn}
        onReorderColumns={reorderColumns}
        onApply={handleApplyColumnSettings}
        onReset={handleResetColumnSettings}
        saving={columnSettingsSaving}
        isCustomized={isCustomized}
        roleFilter={roleFilter}
      />
    </div>
  );
};

export default ProjectsOverviewPage;
